import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { LocalStorageUtils } from '../utils/user.utils';
import { eTypeUser } from '../constants/options-system';
import { ROUTES } from '../constants/app.constants';

/** Home de /dashboard/company según el tipo de usuario autenticado. */
export function getCompanyHomeUrl(userTypeId?: number): string {
  return userTypeId === eTypeUser.AdminBusiness
    ? '/dashboard/company/graficas-business'
    : '/dashboard/company';
}

export function getCurrentUserTypeId(authService: AuthService): number | undefined {
  const currentUser = authService.getCurrentUser()
    || LocalStorageUtils.getUserData(true)
    || LocalStorageUtils.getUserData(false);
  return currentUser?.user_type_id;
}

const NOT_FOUND_MESSAGE = 'La página a la que intentas acceder no existe o aún no está disponible.';
const NOT_FOUND_SUMMARY = 'Página no encontrada';

/**
 * Atrapa cualquier ruta de /dashboard/company/* que no tenga página implementada
 * (p.ej. una opción de menú dada de alta en system_option cuya pantalla aún no
 * existe, o una URL mal escrita): avisa con un toast y vuelve al home del usuario.
 */
export const notFoundGuard: CanActivateFn = () => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const toastService = inject(ToastService);

  toastService.HanddleWarningMessage(NOT_FOUND_MESSAGE, NOT_FOUND_SUMMARY);

  return router.parseUrl(getCompanyHomeUrl(getCurrentUserTypeId(authService)));
};

/**
 * Comodín global (rutas desconocidas fuera de /dashboard/company, p.ej. /foo).
 * Sin sesión activa manda al login como siempre; con sesión, avisa con un toast
 * y manda directo al home del tipo de usuario en vez de pasar por el login.
 */
export const appNotFoundGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const toastService = inject(ToastService);

  const isAuthenticated = await authService.isUserAuthenticated();
  if (!isAuthenticated) {
    return router.parseUrl(ROUTES.AUTH.COMPANY.LOGIN);
  }

  toastService.HanddleWarningMessage(NOT_FOUND_MESSAGE, NOT_FOUND_SUMMARY);

  return router.parseUrl(getCompanyHomeUrl(getCurrentUserTypeId(authService)));
};
